import type { CSSProperties } from 'react';
import PositionSizeTab from './tabs/PositionSizeTab';
import DrawdownTab from './tabs/DrawdownTab';
import RuinTab from './tabs/RuinTab';

export type RiskTabId = 'risk' | 'drawdown' | 'ruin';

const TABS: { id: RiskTabId; label: string; icon: string; accent: string; ambient: string }[] = [
  { id: 'risk', label: 'Máy tính rủi ro', icon: 'ri-crosshair-line', accent: '#10b981', ambient: 'rgba(16,185,129,0.05)' },
  { id: 'drawdown', label: 'Máy tính Drawdown', icon: 'ri-arrow-down-line', accent: '#f43f5e', ambient: 'rgba(244,63,94,0.05)' },
  { id: 'ruin', label: 'Rủi ro phá sản', icon: 'ri-alert-line', accent: '#f59e0b', ambient: 'rgba(245,158,11,0.06)' },
];

export default function RiskLab({ activeTab, onChange }: { activeTab: RiskTabId; onChange: (t: RiskTabId) => void }) {
  const active = TABS.find((t) => t.id === activeTab) ?? TABS[0];

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-slate-200/80 bg-white/70 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.18)] backdrop-blur-xl">
      {/* Ambient tint that follows the active tab */}
      <div
        className="pointer-events-none absolute inset-0 transition-colors duration-500"
        style={{ background: active.ambient }}
      />
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl transition-colors duration-700"
        style={{ background: `${active.accent}14` }}
      />

      <div className="relative">
        {/* Header */}
        <div className="flex flex-col gap-5 border-b border-slate-200/70 px-6 py-6 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex items-center gap-3">
            <span
              className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition-colors duration-500"
              style={{ backgroundColor: active.accent }}
            >
              <i className="ri-shield-check-line text-xl" />
            </span>
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">Bộ máy tính rủi ro</h2>
              <p className="text-xs text-slate-400">Máy tính rủi ro · Máy tính Drawdown · Rủi ro phá sản</p>
            </div>
          </div>

          {/* Smooth capsule tabs */}
          <div className="flex flex-wrap gap-1 rounded-full bg-slate-100 p-1">
            {TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onChange(tab.id)}
                  className={`relative flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                    isActive ? 'text-white' : 'text-slate-500 hover:text-slate-800'
                  }`}
                  style={isActive ? { backgroundColor: tab.accent } : undefined}
                >
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content with fade/slide transition */}
        <div key={activeTab} className="animate-fade-up p-6 md:p-8" style={{ '--tab-accent': active.accent } as CSSProperties}>
          {activeTab === 'risk' && <PositionSizeTab onNext={() => onChange('drawdown')} />}
          {activeTab === 'drawdown' && <DrawdownTab onNext={() => onChange('ruin')} />}
          {activeTab === 'ruin' && <RuinTab onNext={() => onChange('risk')} />}
        </div>
      </div>
    </div>
  );
}